import { Actions, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { filter, forkJoin, map, Observable, switchMap, take } from 'rxjs';

import { SurveyCreationCoreApiActions } from '../survey-creation-core.actions';
// Cross-listen: the launch slice owns the sibling sync outcome.
import { SurveyCreationLaunchApiActions } from '../../launch/survey-creation-launch.actions';

// The next save's outcome: emits once if it succeeded, completes empty if not.
export function surveySaved$(actions$: Actions): Observable<Action> {
  return actions$.pipe(
    ofType(
      SurveyCreationCoreApiActions.saveSurveySuccess,
      SurveyCreationCoreApiActions.saveSurveyFailure,
    ),
    take(1),
    filter(
      ({ type }) =>
        type === SurveyCreationCoreApiActions.saveSurveySuccess.type,
    ),
  );
}

// The next sibling round's outcome: emits once if every sibling saved.
export function siblingsSaved$(actions$: Actions): Observable<Action> {
  return actions$.pipe(
    ofType(SurveyCreationCoreApiActions.saveSiblingSurveysComplete),
    take(1),
    filter(({ allSaved }) => allSaved),
  );
}

// The survey saved, and then every sibling saved after it.
export function surveyAndSiblingsSaved$(actions$: Actions): Observable<Action> {
  return surveySaved$(actions$).pipe(switchMap(() => siblingsSaved$(actions$)));
}

// Traditional MC on the launch page: done once the siblings hold the copied settings too.
export function localeSyncSucceeded$(actions$: Actions): Observable<Action> {
  return actions$.pipe(
    ofType(
      SurveyCreationLaunchApiActions.localeSettingsSynced,
      SurveyCreationLaunchApiActions.localeSettingsSyncFailure,
      SurveyCreationCoreApiActions.saveSurveyFailure,
    ),
    take(1),
    filter(
      ({ type }) =>
        type === SurveyCreationLaunchApiActions.localeSettingsSynced.type,
    ),
  );
}

// Traditional MC on the launch page: the survey saved, then its siblings both
// saved their own targeting and hold the copied settings. Both rounds start on
// the same save, so they are awaited together rather than one after the other.
export function surveySiblingsAndLocalesSaved$(
  actions$: Actions,
): Observable<Action> {
  return surveySaved$(actions$).pipe(
    switchMap(() =>
      forkJoin([siblingsSaved$(actions$), localeSyncSucceeded$(actions$)]),
    ),
    map(([siblingsSaved]) => siblingsSaved),
  );
}
